import React, { useState } from 'react';
import { FileText, Info, ChevronDown, ChevronUp, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * ComposeSection - Section 7: Draft Assembly
 *
 * Fields:
 * - Essay Style selector (Analytical / Reflective / Purpose-Driven / Creative / Hybrid)
 * - Closing Line (student-written, 120 chars max)
 *
 * Features:
 * - Voice Checkpoint #2 (pre-generation)
 * - Generate Draft button → creates full essay from all sections
 */
export default function ComposeSection({ data, onChange, collegeData, allSectionData, essayId }) {
  const [showGuidance, setShowGuidance] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedDraft, setGeneratedDraft] = useState(null);

  const essayStyles = [
    {
      value: 'analytical',
      label: 'Analytical',
      description: 'Logical, cause-and-effect structure. "Because X, I want Y."'
    },
    {
      value: 'reflective',
      label: 'Reflective',
      description: 'Introspective journey. "I used to think... now I see..."'
    },
    {
      value: 'purpose_driven',
      label: 'Purpose-Driven',
      description: 'Starts with mission/goal. "I want to solve X, and here\'s why this school enables that."'
    },
    {
      value: 'creative',
      label: 'Creative',
      description: 'Opens with a scene or metaphor, then reveals connection to school.'
    },
    {
      value: 'hybrid',
      label: 'Hybrid',
      description: 'Combines elements from multiple styles for a unique structure.'
    }
  ];

  // Check if ready to generate
  const canGenerate = () => {
    // Must have content in Sections 1-4
    const hasEthos = allSectionData?.ethos?.motto && allSectionData?.ethos?.buzzwords?.length > 0;
    const hasAcademics = (
      allSectionData?.academics?.courses?.length > 0 ||
      allSectionData?.academics?.professors?.length > 0 ||
      allSectionData?.academics?.initiatives?.length > 0
    );
    const hasCampus = (
      allSectionData?.campus?.spaces?.length > 0 ||
      allSectionData?.campus?.clubs?.length > 0
    );
    const hasPersonal = allSectionData?.personal?.micro_moment || allSectionData?.personal?.motivations?.length > 0;

    return hasEthos && (hasAcademics || hasCampus) && hasPersonal && data.essay_style && data.closing_line;
  };

  // Generate draft
  const generateDraft = async () => {
    if (!canGenerate()) {
      toast.error('Please complete Sections 1-4 and add a closing line first');
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch('/api/why-school/assemble-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          essay_id: essayId,
          college_id: collegeData.id,
          essay_style: data.essay_style,
          closing_line: data.closing_line,
          sections: {
            ethos: allSectionData?.ethos || {},
            academics: allSectionData?.academics || {},
            campus: allSectionData?.campus || {},
            personal: allSectionData?.personal || {},
            rhetoric: allSectionData?.rhetoric || {},
            bridges: allSectionData?.bridges || {}
          },
          college_name: collegeData?.global?.name || 'this school'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate draft');
      }

      const result = await response.json();
      setGeneratedDraft(result.draft);

      // Store in data
      onChange({
        ...data,
        generated_draft: result.draft,
        voice_score: result.voice_score,
        unswitchability_score: result.unswitchability_score,
        generated_at: new Date().toISOString()
      });

      toast.success('Draft generated! Review and edit as needed.');
    } catch (err) {
      console.error('Error generating draft:', err);
      toast.error('Could not generate draft. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Now that you've gathered all your research and personal stories, it's time to assemble
              them into a coherent essay. Choose a style that matches your natural voice, write a
              strong closing line, and let AI create a draft you can refine.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Remember:</p>
              <p>
                The generated draft is a starting point, not the final product. You'll want to review
                it for voice, flow, and authenticity—and make it truly yours.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Field 1: Essay Style Selector */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Essay Style
        </label>
        <p className="text-sm text-gray-600 mb-4">
          Choose the structure that best fits your natural thinking and writing style.
        </p>

        <div className="space-y-3">
          {essayStyles.map((style) => (
            <button
              key={style.value}
              onClick={() => onChange({ ...data, essay_style: style.value })}
              className={`w-full text-left p-4 rounded-lg border-2 transition ${
                data.essay_style === style.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 bg-white hover:border-indigo-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-semibold text-gray-900">{style.label}</span>
                    {data.essay_style === style.value && (
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{style.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Field 2: Closing Line */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Closing Line
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Write a strong final sentence that ties everything together. This should feel definitive
          and personal—avoid generic statements like "I can't wait to attend."
        </p>
        <input
          type="text"
          value={data.closing_line || ''}
          onChange={(e) => onChange({ ...data, closing_line: e.target.value })}
          placeholder="e.g., At MIT, I won't just build things—I'll question why they need to exist."
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Voice Checkpoint #2 - Placeholder */}
      {data.essay_style && data.closing_line && !generatedDraft && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center space-x-2">
            <CheckCircle2 className="w-5 h-5" />
            <span>Pre-Generation Voice Check</span>
          </h4>
          <p className="text-sm text-purple-800 mb-3">
            Before we generate your draft, let's make sure your work maintains your authentic voice.
          </p>
          <div className="space-y-2 text-sm text-purple-800">
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✓</span>
              <span>Sections 1-4 complete</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✓</span>
              <span>Essay style selected</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✓</span>
              <span>Closing line written</span>
            </div>
          </div>
        </div>
      )}

      {/* Generate Draft Button */}
      <div className="border-t border-gray-200 pt-6">
        <div className="flex flex-col items-center space-y-4">
          {!generatedDraft && (
            <>
              <button
                onClick={generateDraft}
                disabled={!canGenerate() || generating}
                className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-lg font-semibold">Generating Your Draft...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6" />
                    <span className="text-lg font-semibold">Generate Complete Draft</span>
                  </>
                )}
              </button>

              {!canGenerate() && (
                <p className="text-sm text-amber-600 text-center">
                  ⚠ Complete Sections 1-4, select an essay style, and write a closing line to generate your draft
                </p>
              )}
            </>
          )}

          {generatedDraft && (
            <div className="w-full">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2 text-green-800 mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Draft Generated!</span>
                </div>
                <p className="text-sm text-green-700">
                  Your essay has been assembled. Review it below and make any edits to ensure it sounds like you.
                </p>
              </div>

              {/* Draft Display */}
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Your Draft</h3>
                  <button
                    onClick={generateDraft}
                    disabled={generating}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    ↻ Regenerate
                  </button>
                </div>

                <textarea
                  value={data.generated_draft || ''}
                  onChange={(e) => onChange({ ...data, generated_draft: e.target.value })}
                  rows={20}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none font-serif text-base leading-relaxed"
                  placeholder="Your generated essay will appear here..."
                />

                {/* Voice Score Display (if available) */}
                {data.voice_score && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-blue-900">Voice Score</p>
                        <p className="text-xs text-blue-700">How authentic does this sound?</p>
                      </div>
                      <span className="text-2xl font-bold text-blue-700">
                        {Math.round(data.voice_score * 100)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          After generating your draft, read it aloud. If any sentence doesn't sound like something
          you'd actually say, revise it. The best "Why School" essays sound like a conversation
          with an admissions officer who asked, "So why here?"
        </p>
      </div>
    </div>
  );
}
